import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { ListChecks } from 'lucide-react'
import { startOfDay } from 'date-fns'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { useProjects } from '@/routes/features/tasks/hooks/useProjects'
import { useTodayTasks } from '@/routes/features/tasks/hooks/useTodayTasks'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { resolveTaskIdentifier } from '@/routes/features/tasks/utils/taskIdentifier'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import useLoginStore from '@/store/LoginStore'

/**
 * Today's note starts useful (design 10a): a live list of what's due today and
 * overdue. It's read from tasks, not stored in the note, so it's always current.
 */
export function DueTodayPanel() {
  const { t } = useTranslation('notes')
  const { date: formatDate } = useDateTimeFormatters()
  const navigate = useNavigate()
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const { tasks } = useTodayTasks()
  const { projects } = useProjects()
  const { all } = useStatuses()

  const progressTypeById = useMemo(
    () => new Map(all.statuses.map((s) => [s.id, s.progressType])),
    [all.statuses],
  )
  const projectKeyById = useMemo(() => new Map(projects.map((p) => [p.id, p.projectKey])), [projects])
  const sorted = useMemo(() => [...tasks].sort((a, b) => (a.dueDate ?? 0) - (b.dueDate ?? 0)), [tasks])
  const startOfToday = startOfDay(new Date()).getTime()

  return (
    <section className="mb-6 rounded-lg border bg-secondary/40 p-2">
      <div className="flex items-center gap-1.5 px-2 pb-1.5 pt-1 text-[12.5px]">
        <ListChecks className="size-3.5 text-muted-foreground" />
        <span className="font-semibold">{t('dueToday')}</span>
        <span className="text-muted-foreground">· {t('live')}</span>
      </div>
      {sorted.length === 0 ? (
        <p className="px-2 pb-1.5 text-[13px] text-muted-foreground">{t('nothingDue')}</p>
      ) : (
        <ul className="divide-y overflow-hidden rounded-md border bg-card">
          {sorted.map((task) => {
            const overdue = task.dueDate != null && task.dueDate < startOfToday
            return (
              <li key={task.id}>
                <button
                  type="button"
                  onClick={() =>
                    void navigate({ to: '/$workspaceSlug/tasks/$taskId', params: { workspaceSlug, taskId: task.id } })
                  }
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary/40"
                >
                  <StatusIcon progressType={progressTypeById.get(task.statusId) ?? 'BL'} statusId={task.statusId} size="sm" />
                  <span className="shrink-0 font-mono text-[10.5px] text-muted-foreground">
                    {resolveTaskIdentifier(task, task.projectId ? projectKeyById.get(task.projectId) : null)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13.5px]">{task.name}</span>
                  {overdue ? (
                    <span className="shrink-0 text-xs font-medium text-brand">{t('overdue')}</span>
                  ) : task.dueDate != null && task.dueDateType === 'datetime' ? (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatDate(new Date(task.dueDate), { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
